import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthProvider';
import { isTokenExpired, isTokenExpiringSoon } from '../utils/tokenUtils';

export const useTokenManager = () => {
  const { user, refreshToken, logout } = useAuth();
  const refreshTimeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);

  const scheduleTokenRefresh = useCallback(async () => {
    if (!user?.access_token) return;

    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Check if token is expired
    if (isTokenExpired(user.access_token)) {
      console.log('Token is expired, attempting refresh...');
      await attemptRefresh();
      return;
    }

    // Check if token will expire soon (within 5 minutes)
    if (isTokenExpiringSoon(user.access_token, 5)) {
      console.log('Token expires soon, refreshing...');
      await attemptRefresh();
      return;
    }

    // Schedule refresh 5 minutes before expiration
    const tokenExp = new Date(user.access_token.split('.')[1] ? 
      JSON.parse(atob(user.access_token.split('.')[1])).exp * 1000 : 0);
    const now = new Date();
    const timeUntilRefresh = Math.max(0, tokenExp.getTime() - now.getTime() - (5 * 60 * 1000)); // 5 minutes before

    if (timeUntilRefresh > 0) {
      refreshTimeoutRef.current = setTimeout(async () => {
        await attemptRefresh();
      }, timeUntilRefresh);
    }
  }, [user?.access_token]);

  const attemptRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return; // Prevent multiple simultaneous refreshes

    try {
      isRefreshingRef.current = true;
      console.log('Attempting to refresh token...');
      await refreshToken();
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh token:', error);
      logout();
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshToken, logout]);

  // Set up periodic token validation
  useEffect(() => {
    if (!user?.access_token) return;

    // Initial setup
    scheduleTokenRefresh();

    // Set up periodic check every 2 minutes
    const intervalId = setInterval(() => {
      if (user?.access_token) {
        scheduleTokenRefresh();
      }
    }, 2 * 60 * 1000); // 2 minutes

    return () => {
      clearInterval(intervalId);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [user?.access_token, scheduleTokenRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  return {
    isRefreshing: isRefreshingRef.current,
    attemptRefresh
  };
}; 